import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ProgressScore as ProgressScoreComponent } from "heat-next";

import { useModal } from "@/context/modal/modal";
import { useSessionDetailsContext } from "@/context/session-details/session-details";

import { ComponentWrapper } from "@/pages/shared/shared.styles";

import ContentLoader from "@/components/content-loader/content-loader";
import EmptyState from "@/components/empty-state/empty-state";
import { EmptyStateType } from "@/components/empty-state/empty-state.types";
import FlyingCircuitsModalContent from "@/components/ui/modal-with-context/modal-contents/session-details-page/flying-circuits-modal-content/flying-circuits-modal-content";
import {
    CONTENT_HEIGHT_MULTIPLE_USERS,
    CONTENT_HEIGHT_SINGLE_USER,
    MODAL_HEIGHT_PERCENTAGE,
} from "@/components/ui/modal-with-context/modal-contents/utils/modal-content-variables";

import base from "@/themes/base/base";

import { useSessionInfo } from "../../hooks/useSessionInfo";

interface ProgressScoreProps {
    isInsideModal?: boolean;
    subDimensionsData?: any; //for when ProgressScore is rendered inside the modal
    onStatsClickToScroll?: (index: number) => void; //to scroll down to specific stat
}

const ProgressScore: React.FC<ProgressScoreProps> = ({
    isInsideModal = false,
    subDimensionsData,
    onStatsClickToScroll,
}) => {
    const {
        scoreData,
        isFetchingScore,
        isErrorScore,
        subDimensionsData: subDimensionsFlyingCircuitsDataFromContext,
        isFetchingSubDimensions: isFetchingSubDimensionsFlyingCircuitsFromContext,
        circuitFilterOptions,
        setCircuitFilterOptions,
        setSelectedCircuit,
        selectedCircuitIndex,
        setSelectedCircuitIndex,
    } = useSessionDetailsContext();

    const { openModal, setWidth, setHeight } = useModal();

    const { getSessionSubtitleDetails, logoUrl, users } = useSessionInfo();

    //to account for single line or multiple line session info
    const MODAL_NON_SCROLLABLE_CONTENT_HEIGHT =
        users?.length <= 1 ? CONTENT_HEIGHT_SINGLE_USER : CONTENT_HEIGHT_MULTIPLE_USERS;

    const [scrollableContainerHeight, setScrollableContainerHeight] = useState(
        () => window.innerHeight * MODAL_HEIGHT_PERCENTAGE - MODAL_NON_SCROLLABLE_CONTENT_HEIGHT,
    );

    const isEmptyState = () => {
        if (isInsideModal) {
            return !subDimensionsData;
        } else if (
            !isErrorScore &&
            (!scoreData || scoreData === null || scoreData?.dataset?.length === 0)
        ) {
            return true;
        }
    };

    const filterOptions = useMemo(() => {
        const linearProgressScore = scoreData?.datasets?.find((dataset: any) => {
            return dataset.code === "linear-progress-score";
        });

        return linearProgressScore?.data?.map((dataItem: any) => {
            return dataItem.title;
        });
    }, [scoreData]);

    useEffect(() => {
        setCircuitFilterOptions(filterOptions);
    }, [scoreData]);

    //to update Modals' ComponentsScrollableWrapper height, when screen height changes
    useEffect(() => {
        const handleScrollableContainerResize = () => {
            const updatedHeight =
                window.innerHeight * MODAL_HEIGHT_PERCENTAGE - MODAL_NON_SCROLLABLE_CONTENT_HEIGHT;
            setScrollableContainerHeight(updatedHeight);
        };

        window.addEventListener("resize", handleScrollableContainerResize);

        return () => window.removeEventListener("resize", handleScrollableContainerResize);
    }, []);

    //to update the DOM element's height, since styled components are not receiving the updated height
    useEffect(() => {
        const componentsScrollableWrapper = document.getElementById(
            "components-scrollable-wrapper",
        );

        if (componentsScrollableWrapper) {
            componentsScrollableWrapper.style.height = `${scrollableContainerHeight}px`;
        }
    }, [scrollableContainerHeight]);

    const handleModalOpen = useCallback(() => {
        setWidth("77.25682295%"); //for 1104px modal width, in breakpoint 1440px
        setHeight("87.4%");

        setSelectedCircuit(circuitFilterOptions && circuitFilterOptions[0]);
        setSelectedCircuitIndex(0);

        openModal({
            content: (
                <FlyingCircuitsModalContent
                    scrollableContainerHeight={scrollableContainerHeight}
                    logoUrl={logoUrl}
                    getSessionSubtitleDetails={getSessionSubtitleDetails}
                    subDimensionsData={subDimensionsFlyingCircuitsDataFromContext}
                />
            ),
            backgroundColor: `${base.colors.neutral.grey[900]}`,
            borderRadius: "0.25rem",
            padding: "1.5rem 1.5rem 0 1.5rem",
            boxShadow: "0px 0px 4px 1px rgba(255, 255, 255, 0.25)",
        });
    }, [
        circuitFilterOptions,
        scrollableContainerHeight,
        logoUrl,
        getSessionSubtitleDetails,
        selectedCircuitIndex,
        subDimensionsFlyingCircuitsDataFromContext,
    ]);

    if (isFetchingScore) {
        return <ContentLoader height="21.1875rem" />;
    }

    if (isEmptyState()) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-details:progress-score:empty-state:title"
                    emptyStateType={EmptyStateType.Charts}
                    minHeight="18.1875rem"
                />
            </ComponentWrapper>
        );
    }

    if (isErrorScore && !isFetchingScore) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="error-state:title"
                    description="error-state:description"
                    emptyStateType={EmptyStateType.Error}
                    minHeight="18.1875rem"
                />
            </ComponentWrapper>
        );
    }

    return (
        <ProgressScoreComponent
            data={isInsideModal ? subDimensionsData : scoreData}
            onClick={handleModalOpen}
            isInsideModal={isInsideModal}
            disableButton={isFetchingSubDimensionsFlyingCircuitsFromContext}
            onStatsClickToScroll={onStatsClickToScroll}
        />
    );
};

export default ProgressScore;
